import { useGetOrganizationDomesticContentQuery } from '@/services/pricing';
import { Box } from '@palmetto/palmetto-components';
import { useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { PriceSheetStatus, ProgramType } from 'types';

import { ActiveDomesticContent } from './ActiveDomesticContent';
import { ArchivedDomesticContent } from './ArchivedDomesticContent';
import type { DomesticContentModifier } from './types';

export const DomesticContentTab = () => {
  const { alias = '', programType = '' } = useParams<{
    alias: string;
    programType: ProgramType;
  }>();

  const { data, isLoading, isFetching } = useGetOrganizationDomesticContentQuery(
    { alias, programType },
    { skip: !alias || !programType },
  );

  const { activeModifiers, archivedModifiers } = useMemo(() => {
    if (!data?.domesticContentModifiers?.length) {
      return { activeModifiers: [], archivedModifiers: [] };
    }

    const sortByStateAndDate = (a: DomesticContentModifier, b: DomesticContentModifier) => {
      // First sort by state alphabetically
      const stateComparison = a.state.localeCompare(b.state);
      if (stateComparison !== 0) return stateComparison;

      // Then by created date descending (newest first)
      const bDateStr = b.createdDate ?? b.createdBy?.date;
      const aDateStr = a.createdDate ?? a.createdBy?.date;
      const bTime = bDateStr ? new Date(bDateStr).getTime() : 0;
      const aTime = aDateStr ? new Date(aDateStr).getTime() : 0;
      return bTime - aTime;
    };

    const active = data.domesticContentModifiers
      .filter((dc: DomesticContentModifier) => dc.status === PriceSheetStatus.active)
      .toSorted(sortByStateAndDate);

    const archived = data.domesticContentModifiers
      .filter((dc: DomesticContentModifier) => dc.status === PriceSheetStatus.archived)
      .toSorted(sortByStateAndDate);

    return { activeModifiers: active, archivedModifiers: archived };
  }, [data]);

  const hasNoSettings = !activeModifiers.length && !archivedModifiers.length;

  if (!isLoading && !isFetching && hasNoSettings) {
    return (
      <Box padding="2xl" textAlign="center" color="grey-500">
        No active domestic content pricing found
      </Box>
    );
  }

  return (
    <Box flex="auto" gap="lg">
      <ActiveDomesticContent
        modifiers={activeModifiers}
        alias={alias}
        programType={programType as ProgramType}
        isLoading={isLoading || isFetching}
      />
      <ArchivedDomesticContent modifiers={archivedModifiers} isLoading={isLoading || isFetching} />
    </Box>
  );
};
